<template>
  <div v-if="service && serviceSlug">
    <div class="container bg-white mx-auto pr-0 pb-6">
      <div class="grid lg:grid-cols-2">
        <div class="pr-3 md:pr-8">
          <span class="crumb">
            <Breadcrumb
              category="Services"
              categoryLink="/services"
              :title="service.ServiceName"
              :titleLink="`/services/${service.ServiceSlug}`"
            />
          </span>
          <div :class="[getServiceClass]">
            <ServicesIcon :cmsImgUrl="$config.public.sgCmsImage" :icon="serviceSlug.icon_class_name" />
            <h1>{{ service.ServiceName }}</h1>
          </div>

          <div class="">
            <div v-html="serviceSlug.description"></div>
            <div
              v-if="
                serviceSlug.benefit_results &&
                serviceSlug.benefit_results.length > 0 &&
                (serviceSlug.benefit_results.length > 1 ||
                  serviceSlug.benefit_results[0].length > 0)
              "
            >
              <ul>
                <li
                  v-for="(benefit, index) in serviceSlug.benefit_results"
                  :key="index"
                >
                  {{ benefit.content }}
                </li>
              </ul>
            </div>
          </div>
          <ServicesCardList
            :services="[service]"
            size="tiny"
          />
        </div>
        <div>
          <div
            class="hero-top"
            :style="`background-image: url(${service.ServiceImage});`"
          ></div>
          <div
            class="hero-bottom"
            :style="`background-image: url(${service.ServiceImage});`"
          >
            <div class="bg-white curveTopRight"></div>
          </div>
        </div>
        
      </div>
    </div>

    <div class="relative">
      <div class="container bg-white mx-auto pr-0">
        <div class="grid grid-cols-1 md:grid-cols-5 gap-6">
          <div class="col-span-3 pr-3">
            <p
              v-if="serviceSlug.description2"
              v-html="serviceSlug.description2"
            ></p>

            <div v-if="service.ServiceVideo1">
              <div v-html="service.ServiceVideo1" />
              <!--
              <p>
                Check out our {{ service.ServiceName }} video to learn more
                about protecting your home from this annoying pest.
              </p>
              -->
            </div>

            <!--
            <div class="bg-grey rounded p-8 text-center w-9/12 my-16 mx-auto" v-if="service.mainPrice === 0">
              <h3 class="mb-3">
                {{ service.ServiceName }} services may not be available in your area.
              </h3>
              <hr class="w-8/12 mx-auto mb-3" />
              <p>Contact your local Spring Green office for details.</p>
              <p class="mb-0">
                <NuxtLink to="/about-us/contact-spring-green" class="button">
                  Contact Us
                </NuxtLink>
              </p>
            </div>
            -->
          </div>

          <div
            class="w-100 col-span-3 md:col-span-2 relative"
            v-if="relatedServices"
          >
            <ServicesRelatedSidebar
              :services="relatedServices"
              servicesTitle="Related Services"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="container mx-auto bg-white p-0" v-if="guides">
      <Curves background="4EA43A" foreground="fff" location="bottomLeft" />
      <div class="green-gradient-bend">
        <GuidesCarousel :guides="guides" />
      </div>
      <Curves background="295F0F" foreground="fff" location="topLeft" />
    </div>

    <div class="container bg-white mx-auto pt-5 pb-10">
      <FaqList :uP_ServiceDefinition_IDs="[service.ServiceDefId]"/>
    </div>
  </div>
</template>


<script setup lang="ts">
import FaqList from "~/components/Faqs/FaqList.vue";

const { $services, $sg, $userContext, $config } = useNuxtApp();
const route = useRoute();
const router = useRouter();
const runtimeConfig = useRuntimeConfig();

const slug = computed(() => route.params.slug as string);

const { data: serviceSlugResponse } = await useAsyncData(
  () => $sg.getOctoberData(`services/${slug.value}/slug`),
  { watch: [slug] }
);

const serviceSlug = computed(() => serviceSlugResponse.value?.[0] ?? null);

watchEffect(() => {
  if (!serviceSlugResponse.value) return;
  if (serviceSlug.value?.redirect_url) {
    navigateTo(`/services/${serviceSlug.value.redirect_url}`);
    return;
  }
  if (!serviceSlug.value) {
    navigateTo("/services");
  }
});

const service = computed(() => $services.getServiceBySlug(slug.value));

const relatedServices = computed(() => {
  if (!serviceSlug.value?.related_services) return null;
  return $services.getServicesByName(serviceSlug.value.related_services);
});

const guides = computed(() => {
  if (!serviceSlug.value) return null;
  return $sg.getConnectedGuides(
    serviceSlug.value.connected_guides_type,
    serviceSlug.value.connected_lawn_care_guides,
    serviceSlug.value.connected_pest_control_guides,
    serviceSlug.value.connected_tree_care_guides
  );
});

const getServiceClass = computed(() => {
  if (serviceSlug.value?.service_color) {
    let classes = "hero-green hero-green-full";
    if (serviceSlug.value?.service_color === "blue") {
      classes = "hero-blue hero-blue-full";
    } else if (serviceSlug.value?.service_color === "orange") {
      classes = "hero-orange hero-orange-full";
    }
    return classes;
  } else if (service.value) {
    let classes = "hero-green hero-green-full";
    if (service.value.ServiceCategory === "4") {
      classes = "hero-blue hero-blue-full";
    } else if (
      service.value.ServiceCategory === "5" ||
      service.value.ServiceCategory === "6"
    ) {
      classes = "hero-orange hero-orange-full";
    }
    return classes;
  }
  return "hero-green hero-green-full";
});

const frontEndUrl = computed(() =>
  (runtimeConfig.public?.frontEndUrl || "https://www.spring-green.com").replace(/\/$/, "")
);

const serviceUrl = computed(() => {
  const path = route.path.endsWith("/") ? route.path : `${route.path}/`;
  return `${frontEndUrl.value}${path}`;
});

const socialImage = computed(() =>
  serviceSlug.value?.social_image
    ? `${$config.public.sgCmsImage}${serviceSlug.value.social_image}`
    : undefined
);

watchEffect(() => {
  if (!serviceSlug.value) return;

  const serviceSchema = buildServiceSchema({
    name: serviceSlug.value.name || service.value?.ServiceName || "",
    description:
      serviceSlug.value.meta_description ||
      serviceSlug.value.excerpt ||
      serviceSlug.value.description,
    url: serviceUrl.value,
    image: socialImage.value || service.value?.ServiceImage,
    provider: {
      name: "Spring-Green",
      url: frontEndUrl.value,
    },
  });

  useSeoHead({
    title: serviceSlug.value.seo_title || serviceSlug.value.name,
    description: serviceSlug.value.meta_description || serviceSlug.value.excerpt,
    robotsIndex: serviceSlug.value.meta_robots_index,
    robotsFollow: serviceSlug.value.meta_robots_follow,
    openGraph: {
      title:
        serviceSlug.value.social_title ||
        serviceSlug.value.seo_title ||
        serviceSlug.value.name,
      description:
        serviceSlug.value.social_description ||
        serviceSlug.value.meta_description ||
        serviceSlug.value.excerpt,
      image: socialImage.value,
      url: serviceUrl.value,
      type: "website",
    },
    twitter: {
      title:
        serviceSlug.value.social_title ||
        serviceSlug.value.seo_title ||
        serviceSlug.value.name,
      description:
        serviceSlug.value.social_description ||
        serviceSlug.value.meta_description ||
        serviceSlug.value.excerpt,
      image: socialImage.value,
      card: socialImage.value ? "summary_large_image" : "summary",
    },
    structuredData: serviceSchema,
    breadcrumbs: [
      { name: "Home", url: `${frontEndUrl.value}/` },
      { name: "Services", url: `${frontEndUrl.value}/services/` },
      { name: service.value?.ServiceName || serviceSlug.value.name, url: serviceUrl.value },
    ],
  });
});

onMounted(async () => {
  await $userContext.metaRefresh({
    refresh: new Set(["ORDER_INFO", "SERVICE_INFO", "PAYMENT_INFO"]),
  });

  if (!service.value) {
    router.push("/services");
  }
});
</script>



<style scoped>
.hero-top {
  border-bottom-left-radius: 70px;
  background-position: right top;
  background-size: 804px auto;
  background-repeat: no-repeat;
  height: 470px;
  padding: 60px 0;
  border-bottom-left-radius: 70px;
  background-size: 804px auto;
}

@media screen and (max-width: 1023px) {
  .hero-top {
    border-top-left-radius: 70px;
    margin-top: 1rem;
  }
}

.hero-bottom {
  background-repeat: no-repeat;
  background-size: 804px auto;
  background-position: right -470px;
  height: 70px;
}
</style>
